import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const DeveloperNotes = () => {
  const [visible, setVisible] = useState(false);
  const [notes, setNotes] = useState([
    { id: 1, text: "TODO: Make this work", color: "#ffeb3b" },
    { id: 2, text: "Why did we do it this way?", color: "#ff9800" },
    { id: 3, text: "Delete this before production", color: "#f44336" },
    {
      id: 4,
      text: "This code is held together with duct tape and prayers",
      color: "#4caf50",
    },
    { id: 5, text: "I wrote this at 3am, don't judge me", color: "#2196f3" },
    { id: 6, text: "Here be dragons", color: "#9c27b0" },
    { id: 7, text: "If this works, it's a miracle", color: "#e91e63" },
  ]);
  const [newNoteText, setNewNoteText] = useState("");
  // eslint-disable-next-line
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Load notes from localStorage
    const savedNotes = localStorage.getItem("developerNotes");
    if (savedNotes) {
      try {
        setNotes(JSON.parse(savedNotes));
      } catch (e) {
        console.error("Failed to parse saved notes", e);
      }
    }

    // Add event listener for Shift+Left-click instead of Shift+Right-click
    const handleClick = (e) => {
      if (e.shiftKey && e.button === 0) {
        e.preventDefault();
        setClickPosition({ x: e.clientX, y: e.clientY });
        setVisible(true);
      }
    };

    // Add event listener for Escape key to close overlay
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && visible) {
        setVisible(false);
      }
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible]);

  // Save notes to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("developerNotes", JSON.stringify(notes));
  }, [notes]);

  const handleAddNote = () => {
    if (newNoteText.trim()) {
      const colors = [
        "#ffeb3b",
        "#ff9800",
        "#f44336",
        "#4caf50",
        "#2196f3",
        "#9c27b0",
        "#e91e63",
      ];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];

      setNotes([
        ...notes,
        {
          id: Date.now(),
          text: newNoteText,
          color: randomColor,
        },
      ]);
      setNewNoteText("");
    }
  };

  const handleDeleteNote = (id) => {
    setNotes(notes.filter((note) => note.id !== id));
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        overflow: "auto",
      }}
      onClick={() => setVisible(false)}
    >
      <Box
        sx={{
          backgroundColor: "#333",
          borderRadius: 2,
          padding: 3,
          width: "100%",
          maxWidth: 600,
          maxHeight: "80vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ color: "white" }}>
            Developer's Notes
          </Typography>
          <IconButton onClick={() => setVisible(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 3, display: "flex" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Add a new note..."
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            sx={{
              mr: 1,
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddNote();
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNote}
          >
            Add
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {notes.map((note) => (
            <Box
              key={note.id}
              sx={{
                position: "relative",
                width: 180,
                minHeight: 180,
                padding: 2,
                backgroundColor: note.color,
                borderRadius: 1,
                boxShadow: 3,
                display: "flex",
                flexDirection: "column",
                fontFamily: "cursive",
                transform: "rotate(-2deg)",
                "&:nth-of-type(even)": {
                  transform: "rotate(1deg)",
                },
                "&:nth-of-type(3n)": {
                  transform: "rotate(-1deg)",
                },
                "&:hover": {
                  zIndex: 1,
                  transform: "scale(1.05)",
                  transition: "transform 0.2s ease",
                },
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: "cursive",
                  fontSize: "1rem",
                  color: "rgba(0, 0, 0, 0.8)",
                  wordBreak: "break-word",
                }}
              >
                {note.text}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 5,
                  right: 5,
                  color: "rgba(0, 0, 0, 0.6)",
                  "&:hover": {
                    color: "rgba(0, 0, 0, 0.9)",
                  },
                }}
                onClick={() => handleDeleteNote(note.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DeveloperNotes;
